import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, setActiveAccount, getActiveAccount, setViewAs } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=user
  const [ctx, setCtx] = useState(null); // accounts + memberships
  const [caps, setCaps] = useState(null); // canonical feature capabilities (same checks the API enforces)
  const [activeAccount, setActive] = useState(getActiveAccount());

  const loadContext = useCallback(async () => {
    const { data } = await api.get("/auth/context");
    const ids = data.accounts.map((a) => a.id);
    let current = getActiveAccount();
    if (!current || !ids.includes(current)) {
      current = data.accounts[0]?.id || null;
    }
    setActiveAccount(current);
    setActive(current);
    if (current && !data.is_super) {
      try { setCaps((await api.get("/agency/capabilities")).data); } catch { setCaps({}); }
    } else setCaps({});
    setCtx(data);
    return data;
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadContext();
    } catch {
      setUser(false);
    }
  }, [loadContext]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  // Presence heartbeat while a signed-in tab is visible (not CRM activity).
  useEffect(() => {
    if (!user || !ctx) return undefined;
    const beat = () => { if (document.visibilityState === "visible") api.post("/support/presence/heartbeat").catch(() => {}); };
    beat();
    const t = setInterval(beat, 45000);
    document.addEventListener("visibilitychange", beat);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", beat); };
  }, [user, ctx]);

  const onAuthed = async (payload) => {
    if (payload.access_token) setToken(payload.access_token);
    setUser(payload.user);
    await loadContext();
    return payload;
  };

  const login = async (email, password) => {
    setViewAs(null);
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfaToken: data.mfa_token };
    await onAuthed(data);
    return { user: data.user, mfaSetupRequired: data.mfa_setup_required };
  };

  const verifyMfa = async (mfaToken, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
    await onAuthed(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/support/presence/offline"); } catch {}
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setViewAs(null);
    setUser(false);
    setCtx(null);
  };

  const switchAccount = (id) => {
    localStorage.removeItem("tr_district");   // never carry a District across tenants
    setActiveAccount(id);
    setActive(id);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const startViewAs = async ({ role, district_org_id, person_id }) => {
    const { data } = await api.post("/admin/view-as/start", { role, district_org_id, person_id });
    setViewAs(data.id);
    await bootstrap();   // re-pull /auth/me + /auth/context so the whole app re-renders as the role
    return data;
  };

  const exitViewAs = async () => {
    try { await api.post("/admin/view-as/exit"); } catch {}
    setViewAs(null);
    // Hard-restore the admin shell: guarantees the banner, stale preview state and any in-flight
    // portal requests are cleared and normal permissions/navigation return immediately.
    window.location.assign("/");
  };

  const currentAccount = ctx?.accounts?.find((a) => a.id === activeAccount) || null;
  const viewAs = user?.view_as || null;
  // During View As the effective roles are the previewed role only, so the UI matches a real login of that role.
  const roles = viewAs?.role ? new Set([viewAs.role]) : new Set(
    (ctx?.memberships || []).filter((m) => m.client_account_id === activeAccount).map((m) => m.role)
  );
  const isSuper = !viewAs && !!user?.is_platform_super_admin;
  const isAdmin = isSuper || roles.has("account_admin");
  const isGroupAdmin = isSuper || roles.has("group_admin");
  const isUsersAdmin = isAdmin || isGroupAdmin;
  const isManager = isAdmin || roles.has("recruiting_manager");
  const canViewUsers = isUsersAdmin || isManager || isSuper;
  const isOutside = (roles.has("external_recruiter") || roles.has("external_recruiting_manager") || roles.has("external_recruiter_admin")) && !isSuper && !isAdmin && !roles.has("internal_recruiter") && !roles.has("recruiting_manager");
  const isReferralPartner = roles.has("referral_partner") && !isSuper && !isAdmin && !isGroupAdmin && !isManager && !roles.has("internal_recruiter") && !roles.has("external_recruiter");
  const RECRUITING_ROLES = ["account_admin", "group_admin", "recruiting_manager", "internal_recruiter", "external_recruiter", "external_recruiting_manager", "external_recruiter_admin", "recruiting_support"];
  const canRecruiting = isSuper || [...roles].some((r) => RECRUITING_ROLES.includes(r));
  const canTasks = canRecruiting && !isOutside;
  const OFFICE_OK = ["agent", "agent_staff", "candidate", "referral_partner"];
  const isOfficeUser = !isSuper && (roles.has("agent") || roles.has("agent_staff")) && [...roles].every((r) => OFFICE_OK.includes(r));
  const canSchedule = isSuper || !!caps?.scheduling;

  return (
    <AuthContext.Provider
      value={{ user, ctx, activeAccount, currentAccount, roles, isSuper, isAdmin, isManager, isOutside,
        isGroupAdmin, isUsersAdmin, canViewUsers, isReferralPartner,
        canRecruiting, canTasks, isOfficeUser, canSchedule, viewAs, startViewAs, exitViewAs,
        login, verifyMfa, logout, switchAccount, refreshUser, onAuthed, reloadContext: loadContext }}
    >
      {children}
    </AuthContext.Provider>
  );
}
